import { BookChapter, ContentItem, FlowchartStep } from "@/data/bookContent";
import { motion } from "framer-motion";
import { CheckCircle2, XCircle, AlertTriangle, Info, ChevronRight } from "lucide-react";
import { useState } from "react";

interface ChapterSectionProps {
  chapter: BookChapter;
}

function SectionHeading({ text }: { text: string }) {
  return (
    <h3
      className="font-sans text-xl font-bold mt-10 mb-4 pb-2 border-b"
      style={{
        color: 'var(--foreground)',
        borderColor: 'color-mix(in srgb, var(--border) 50%, transparent)'
      }}
    >
      {text}
    </h3>
  );
}

function Paragraph({ text }: { text: string }) {
  return (
    <p
      className="font-sans leading-[1.9] mb-4"
      style={{ color: 'color-mix(in srgb, var(--foreground) 85%, transparent)' }}
    >
      {text}
    </p>
  );
}

function ListGood({ items }: { items: string[] }) {
  return (
    <ul className="space-y-2 my-5">
      {items.map((item, i) => (
        <li key={i} className="flex items-start gap-3">
          <CheckCircle2
            className="w-5 h-5 shrink-0 mt-0.5"
            style={{ color: '#16a34a' }}
          />
          <span
            className="font-sans text-sm leading-relaxed"
            style={{ color: 'color-mix(in srgb, var(--foreground) 85%, transparent)' }}
          >
            {item}
          </span>
        </li>
      ))}
    </ul>
  );
}

function ListBad({ items }: { items: string[] }) {
  return (
    <ul className="space-y-2 my-5">
      {items.map((item, i) => (
        <li key={i} className="flex items-start gap-3">
          <XCircle
            className="w-5 h-5 shrink-0 mt-0.5"
            style={{ color: '#dc2626' }}
          />
          <span
            className="font-sans text-sm leading-relaxed"
            style={{ color: 'color-mix(in srgb, var(--foreground) 85%, transparent)' }}
          >
            {item}
          </span>
        </li>
      ))}
    </ul>
  );
}

function ListNumbered({ items }: { items: string[] }) {
  return (
    <ol className="space-y-3 my-5">
      {items.map((item, i) => (
        <li key={i} className="flex items-start gap-3">
          <span
            className="font-sans text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center shrink-0 mt-0.5"
            style={{
              color: 'var(--accent)',
              backgroundColor: 'color-mix(in srgb, var(--accent) 10%, transparent)'
            }}
          >
            {i + 1}
          </span>
          <span
            className="font-sans text-sm leading-relaxed"
            style={{ color: 'color-mix(in srgb, var(--foreground) 85%, transparent)' }}
          >
            {item}
          </span>
        </li>
      ))}
    </ol>
  );
}

function ListPlain({ items }: { items: string[] }) {
  return (
    <ul className="space-y-2 my-5">
      {items.map((item, i) => (
        <li key={i} className="flex items-start gap-3">
          <ChevronRight
            className="w-4 h-4 shrink-0 mt-1"
            style={{ color: 'var(--accent)' }}
          />
          <span
            className="font-sans text-sm leading-relaxed"
            style={{ color: 'color-mix(in srgb, var(--foreground) 85%, transparent)' }}
          >
            {item}
          </span>
        </li>
      ))}
    </ul>
  );
}

function CalloutInfo({ title, text }: { title: string; text: string }) {
  return (
    <div
      className="my-6 rounded-xl border p-5"
      style={{
        borderColor: 'color-mix(in srgb, var(--primary) 20%, transparent)',
        backgroundColor: 'color-mix(in srgb, var(--primary) 5%, transparent)'
      }}
    >
      <div className="flex items-start gap-3">
        <Info
          className="w-5 h-5 shrink-0 mt-0.5"
          style={{ color: 'var(--primary)' }}
        />
        <div>
          {title && (
            <p
              className="font-sans font-semibold text-sm mb-1"
              style={{ color: 'var(--primary)' }}
            >
              {title}
            </p>
          )}
          {text && (
            <p
              className="font-sans text-sm leading-relaxed"
              style={{ color: 'color-mix(in srgb, var(--foreground) 80%, transparent)' }}
            >
              {text}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function CalloutWarning({ title, text }: { title: string; text: string }) {
  return (
    <div
      className="my-6 rounded-xl border p-5"
      style={{
        borderColor: 'rgba(234,179,8,0.3)',
        backgroundColor: 'rgba(254,252,232,0.5)'
      }}
    >
      <div className="flex items-start gap-3">
        <AlertTriangle
          className="w-5 h-5 shrink-0 mt-0.5"
          style={{ color: '#ca8a04' }}
        />
        <div>
          {title && (
            <p
              className="font-sans font-semibold text-sm mb-1"
              style={{ color: '#a16207' }}
            >
              {title}
            </p>
          )}
          {text && (
            <p
              className="font-sans text-sm leading-relaxed"
              style={{ color: 'color-mix(in srgb, var(--foreground) 80%, transparent)' }}
            >
              {text}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function Flowchart({ steps }: { steps: FlowchartStep[] }) {
  return (
    <div className="my-6 space-y-3">
      {steps.map((step) => (
        <div
          key={step.number}
          className="rounded-xl border p-5"
          style={{
            borderColor: 'var(--border)',
            backgroundColor: 'var(--card)',
            boxShadow: '0 2px 8px -2px rgba(0,0,0,0.08)'
          }}
        >
          <div className="flex items-start gap-3">
            <span
              className="font-sans text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center shrink-0 mt-0.5"
              style={{
                color: 'var(--primary-foreground)',
                backgroundColor: 'var(--primary)'
              }}
            >
              {step.number}
            </span>
            <div className="flex-1">
              <p
                className="font-sans font-semibold text-sm mb-3"
                style={{ color: 'var(--foreground)' }}
              >
                {step.question}
              </p>
              <div className="grid grid-cols-2 gap-2">
                <div
                  className="rounded-lg px-3 py-2"
                  style={{
                    backgroundColor: 'rgba(240,253,244,0.8)',
                    border: '1px solid rgba(187,247,208,0.6)'
                  }}
                >
                  <p
                    className="text-xs font-bold mb-0.5"
                    style={{ color: '#15803d' }}
                  >
                    YES
                  </p>
                  <p
                    className="text-xs leading-snug"
                    style={{ color: '#166534' }}
                  >
                    {step.yes}
                  </p>
                </div>
                <div
                  className="rounded-lg px-3 py-2"
                  style={{
                    backgroundColor: 'rgba(254,242,242,0.8)',
                    border: '1px solid rgba(254,202,202,0.6)'
                  }}
                >
                  <p
                    className="text-xs font-bold mb-0.5"
                    style={{ color: '#b91c1c' }}
                  >
                    NO
                  </p>
                  <p
                    className="text-xs leading-snug"
                    style={{ color: '#991b1b' }}
                  >
                    {step.no}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function ChecklistGroup({ title, items }: { title: string; items: string[] }) {
  const [checked, setChecked] = useState<boolean[]>(items.map(() => false));

  const toggle = (i: number) => {
    setChecked((prev) => {
      const next = [...prev];
      next[i] = !next[i];
      return next;
    });
  };

  const count = checked.filter(Boolean).length;

  return (
    <div
      className="my-5 rounded-xl border overflow-hidden"
      style={{
        borderColor: 'var(--border)',
        backgroundColor: 'var(--card)'
      }}
    >
      <div
        className="flex items-center justify-between px-5 py-3 border-b"
        style={{
          backgroundColor: 'color-mix(in srgb, var(--muted) 40%, transparent)',
          borderColor: 'var(--border)'
        }}
      >
        <p
          className="font-sans font-semibold text-sm"
          style={{ color: 'var(--foreground)' }}
        >
          {title}
        </p>
        <span
          className="text-xs font-sans"
          style={{ color: 'var(--muted-foreground)' }}
        >
          {count}/{items.length}
        </span>
      </div>
      <ul className="divide-y" style={{ borderColor: 'var(--border)' }}>
        {items.map((item, i) => (
          <li key={i}>
            <button
              onClick={() => toggle(i)}
              className="w-full flex items-center gap-3 px-5 py-3 transition-colors text-left"
              style={{
                backgroundColor: 'transparent'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'color-mix(in srgb, var(--muted) 20%, transparent)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              <span
                className="w-5 h-5 rounded flex items-center justify-center shrink-0 transition-colors"
                style={{
                  backgroundColor: checked[i] ? '#16a34a' : 'transparent',
                  border: checked[i] ? '2px solid #16a34a' : '2px solid var(--border)'
                }}
              >
                {checked[i] && (
                  <svg
                    className="w-3 h-3"
                    viewBox="0 0 12 12"
                    fill="none"
                    style={{ color: '#ffffff' }}
                  >
                    <path
                      d="M2 6l3 3 5-5"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
              </span>
              <span
                className="font-sans text-sm leading-relaxed transition-colors"
                style={{
                  color: checked[i]
                    ? 'var(--muted-foreground)'
                    : 'color-mix(in srgb, var(--foreground) 85%, transparent)',
                  textDecoration: checked[i] ? 'line-through' : 'none'
                }}
              >
                {item}
              </span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function renderItem(item: ContentItem, index: number) {
  switch (item.type) {
    case "section-heading":
      return <SectionHeading key={index} text={item.text} />;
    case "paragraph":
      return <Paragraph key={index} text={item.text} />;
    case "list-good":
      return <ListGood key={index} items={item.items} />;
    case "list-bad":
      return <ListBad key={index} items={item.items} />;
    case "list-numbered":
      return <ListNumbered key={index} items={item.items} />;
    case "list-plain":
      return <ListPlain key={index} items={item.items} />;
    case "callout-info":
      return <CalloutInfo key={index} title={item.title} text={item.text} />;
    case "callout-warning":
      return <CalloutWarning key={index} title={item.title} text={item.text} />;
    case "flowchart":
      return <Flowchart key={index} steps={item.steps} />;
    case "checklist-group":
      return <ChecklistGroup key={index} title={item.title} items={item.items} />;
    default:
      return null;
  }
}

export function ChapterSection({ chapter }: ChapterSectionProps) {
  return (
    <motion.article
      className="pt-12 pb-4"
      initial={{ opacity: 0, y: 28 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
    >
      <div className="mb-8">
        <h2
          className="font-sans text-3xl md:text-4xl font-bold leading-tight"
          style={{ color: 'var(--foreground)' }}
        >
          {chapter.title}
        </h2>
        <div
          className="w-12 h-0.5 mt-4"
          style={{ backgroundColor: 'var(--accent)' }}
        />
      </div>

      <div>
        {chapter.content.map((item, i) => renderItem(item, i))}
      </div>

      <div
        className="mt-16 border-t"
        style={{ borderColor: 'color-mix(in srgb, var(--border) 40%, transparent)' }}
      />
    </motion.article>
  );
}